import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useSiteClub } from "../lib/SiteClubContext";
import { listEvents, type EventRow } from "../lib/api";
import { formatEventDate } from "../lib/formatDate";

function formatDollars(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`;
}

function EventGrid({ events }: { events: EventRow[] }) {
  return (
    <div className="events-grid single-column">
      {events.map((ev) => (
        <Link key={ev.eventId} to={`/events/${ev.eventId}`} className="card event-card">
          <h3>{ev.title}</h3>
          <p className="muted">{formatEventDate(ev.dateISO, ev.endDateISO)}</p>
          {ev.location && <p className="muted">{ev.location}</p>}
          <p>{formatDollars(ev.entryFeeCents)} entry</p>
        </Link>
      ))}
    </div>
  );
}

/** moreLinkTo: when set (the home page's embedded preview), shows a small
 *  "more…" link next to the heading pointing at the full events list, and
 *  skips the "Prior events" section -- the preview is upcoming-only, the
 *  past list only makes sense on the full page.
 *  Left unset on the full list itself (/events) -- a "more…" link there
 *  would just point back at the same content. */
/** Lower-cased, whitespace-collapsed text for case-insensitive matching. */
function norm(s: string | undefined): string {
  return (s ?? "").toLowerCase().replace(/\s+/g, " ").trim();
}

/** Local calendar day (YYYY-MM-DD) of an ISO timestamp. */
function localDay(iso: string): string {
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/** True when the event runs on `day` (YYYY-MM-DD, local time), counting
 *  every day of a multi-day event. */
function runsOn(ev: EventRow, day: string): boolean {
  return localDay(ev.dateISO) <= day && day <= localDay(ev.endDateISO ?? ev.dateISO);
}

/** Every word in the query has to appear in the title, location, description or a class name. */
function matchesQuery(ev: EventRow, query: string): boolean {
  const words = norm(query).split(" ").filter(Boolean);
  if (words.length === 0) return true;
  const hay = norm([ev.title, ev.location, ev.description, ...(ev.classes ?? []).map((c) => c.name)].join(" "));
  return words.every((w) => hay.includes(w));
}

export default function EventsPage({ moreLinkTo }: { moreLinkTo?: string } = {}) {
  const [events, setEvents] = useState<EventRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [query, setQuery] = useState("");
  const [day, setDay] = useState("");

  const { club, loading: clubLoading } = useSiteClub();

  // On a club's domain, only that club's events.
  useEffect(() => {
    if (clubLoading) return;
    listEvents(club?.clubId)
      .then((res) => setEvents(res.events))
      .catch((err) => setError(err instanceof Error ? err.message : "Failed to load events."));
  }, [club?.clubId, clubLoading]);

  const now = new Date().toISOString();
  // The filter only shows on the full list -- the home page preview stays a
  // plain "what's next" list. Filtering is client-side: listEvents() already
  // returns every published event.
  const showFilters = !moreLinkTo;
  const filtering = showFilters && (query.trim() !== "" || day !== "");
  const filtered = events?.filter((ev) => (!day || runsOn(ev, day)) && matchesQuery(ev, query)) ?? null;
  // listEvents() returns every published event (past and future) sorted
  // ascending by dateISO -- split it here rather than two API calls.
  const upcoming = filtered?.filter((ev) => (ev.endDateISO ?? ev.dateISO) >= now) ?? null;
  const prior = filtered ? [...filtered.filter((ev) => (ev.endDateISO ?? ev.dateISO) < now)].reverse() : null;

  return (
    <div>
      <h1>
        Upcoming events
        {moreLinkTo && (
          <Link to={moreLinkTo} className="muted" style={{ fontSize: "0.85rem", fontWeight: 400, marginLeft: "0.75rem" }}>
            more…
          </Link>
        )}
      </h1>
      {showFilters && (
        <div className="row event-filters" role="search">
          <input
            type="search"
            placeholder="Search by event, track, town or class"
            aria-label="Search events"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          <input type="date" aria-label="Racing on" value={day} onChange={(e) => setDay(e.target.value)} />
          <div className="event-filter-buttons">
            <button type="button" className="secondary" onClick={() => setDay(localDay(new Date().toISOString()))}>
              Today
            </button>
            {filtering && (
              <button
                type="button"
                className="secondary"
                onClick={() => {
                  setQuery("");
                  setDay("");
                }}
              >
                Clear
              </button>
            )}
          </div>
        </div>
      )}
      {error && <p className="err">{error}</p>}
      {!events && !error && <p className="muted">Loading…</p>}
      {upcoming && upcoming.length === 0 && (
        <p className="muted">{filtering ? "No upcoming events match." : "No upcoming events right now."}</p>
      )}
      {upcoming && <EventGrid events={upcoming} />}

      {!moreLinkTo && prior && prior.length > 0 && (
        <>
          <h1 style={{ marginTop: "2rem" }}>Prior events</h1>
          <EventGrid events={prior} />
        </>
      )}
    </div>
  );
}
